import moment from "moment";
import { Card, Grid } from "@mui/material";
import { useEffect, useState, useMemo } from "react";

import Box from "../../components/Box";
import Helpers from "../../commons/helpers";
import Avatar from "../../components/Avatar";
import Strings from "../../constants/strings";
import useStrings from "../../hooks/useStrings";
import ControlChip from "../../components/Chip";
import DataTable from "../../components/DataTable";
import FormField from "../../components/FormField";
import Typography from "../../components/Typography";
import DatePicker from "../../components/DatePicker";
import Autocomplete from "../../components/Autocomplete";
import StatusCell from "../../components/DataTable/StatusCell";
import useContainerInvitation from "../../hooks/useContainerInvitation";
import InvitationService, { IRequestGetPagedInvitation } from "../../services/identity/invitation.service";

import { MainComponent } from "../main";
import { Status } from "../../constants/enums";
import { Constants } from "../../constants/constants";
import { useCommonComponentContext } from "../../providers/CommonComponentProvider";
import { IActionMenuItem, ICodename, IInvitation } from "../../commons/interfaces";


interface IModel {
    requestData?: IRequestGetPagedInvitation;
    requestDataTemp?: IRequestGetPagedInvitation;
}

interface IProps {
    lable?: string;
    request: IRequestGetPagedInvitation;
    extraHeaders?: {
        Header: string,
        accessor: string,
        width?: string | number,
        Cell: (row: any) => JSX.Element,
    }[],
    onGetPaged: (query: string, totalCount: number) => void;
    onNavigate: (data?: { id?: string; mode?: number }) => void;
    deleteInvitation?: (invite: IInvitation, reload: Function) => void;
}

const ManageInvitationContainer = ({ lable, request, extraHeaders = [], onNavigate, onGetPaged, deleteInvitation }: IProps) => {
    const strings = useStrings();

    const { onResendInvitation, onDeleteInvitation } = useContainerInvitation();

    const {
        userInfo,
        tenantCode,
        serviceCode,
        onError,
        onShowLoading,
        onHideLoading,
    } = useCommonComponentContext();

    const [dataRows, setDataRows] = useState<any[]>([]);
    const [model, setModel] = useState<IModel>({} as IModel);
    const [loadingDataTable, setLoadingDataTable] = useState<boolean>(false);
    const [showCopyContentTooltip, setCopyContentTooltip] = useState<boolean>(false);

    const listStatusInvitation: ICodename[] = useMemo(
        () => [
            // { code: Status.Draft, name: strings.Common.DRAFT },
            { code: Status.Accept, name: strings.Common.ACCEPTED },
            { code: Status.Active, name: strings.Common.INVITING },
            // { code: Status.Deleted, name: strings.Common.DELETED },
            { code: Status.Decline, name: strings.Common.REJECTED },
            // { code: Status.Inactive, name: strings.Common.EXPIRED },
        ],
        [strings]
    );

    const statusCell = (status: number, validTo: number) => {
        let data: any;
        switch (status) {
            case Status.Active:
                if (validTo < moment().unix()) {
                    data = { icon: "report", color: "secondary", title: strings.Common.EXPIRED };
                } else {
                    data = { icon: "history", color: "warning", title: strings.Common.INVITING };
                }
                break;
            case Status.Accept:
                data = { icon: "done", color: "success", title: strings.Common.ACCEPTED };
                break;
            case Status.Draft:
                data = { icon: "drafts", color: "secondary", title: strings.Common.DRAFT };
                break;
            case Status.Deleted:
                data = { icon: "remove", color: "error", title: strings.Common.DELETED };
                break;
            case Status.Decline:
                data = { icon: "block", color: "error", title: strings.Common.REJECTED };
                break;
            default:
                data = { icon: "sync", color: "warning", title: strings.Common.INVITING };
                break;
        };
        return <StatusCell data={data} />;
    };

    const dataColumns = useMemo(
        () => {
            let temp = [
                {
                    Header: strings.INVITATION.EMAIL, accessor: "invitedEmail", width: "250px",
                    Cell: (row: any) => (
                        <Box display="flex" alignItems="center">
                            <Avatar
                                alt="avt-invi"
                                text={row?.row?.original?.fullName || row?.value}
                                sx={{
                                    width: "36px",
                                    height: "36px",
                                    objectFit: "cover",
                                    marginRight: "8px",
                                    borderRadius: "50%",
                                }}
                            />
                            {Helpers.isNullOrEmpty(row?.row?.original?.fullName)
                                ? (
                                    <Typography color="text" variant="caption" fontWeight="medium" >
                                        {row?.value}
                                    </Typography>
                                )
                                : (
                                    <Box display="grid">
                                        <Typography color="text" variant="caption" fontWeight="medium" >
                                            {row?.row?.original?.fullName}
                                        </Typography>
                                        <Typography color="secondary" variant="caption" >
                                            {row?.value}
                                        </Typography>
                                    </Box>
                                )
                            }
                        </Box>
                    ),
                },
                { Header: strings.Role.ROLE, accessor: "roleNames", width: "150px" },
            ];

            const statusHeader = [
                {
                    Header: strings.INVITATION.INVITING_TIME, accessor: "sendTime",
                    Cell: (row: any) => (
                        <Typography variant="caption" fontWeight="medium" color="text">
                            {Helpers.formatDate(Number(row?.row?.original?.sendTime) * 1000, "HH:MM DD/MM/YYYY")}
                        </Typography>
                    ),
                },
                {
                    Header: strings.Common.STATUS, accessor: "status",
                    Cell: (row: any) => (
                        statusCell(Number(row.value), Number(row.row?.original?.validTo))
                    ),
                },
            ]

            return [...temp, ...extraHeaders, ...statusHeader];
        },
        [strings, extraHeaders]
    );

    const dataTable = useMemo(
        () => ({
            rows: dataRows,
            columns: dataColumns,
        }),
        [dataColumns, dataRows]
    );

    useEffect(() => {
        (
            async () => {

                const newDataReq: IRequestGetPagedInvitation = {
                    totalCount: request.totalCount,
                    pageSize: request.pageSize || model?.requestData?.pageSize,
                    pageNumber: request.pageNumber || model?.requestData?.pageNumber,

                    validFrom: request.validFrom || model?.requestData?.validFrom,
                    validTo: request.validTo || model?.requestData?.validTo,
                    orderBy: request.orderBy || model?.requestData?.orderBy,

                    type: request.type || model?.requestData?.type,
                    email: request.email || model?.requestData?.email,
                    emails: request.emails || model?.requestData?.emails,
                    status: request.status || model?.requestData?.status,
                    roleCodes: request.roleCodes || model?.requestData?.roleCodes,
                    searchText: request.searchText || model?.requestData?.searchText,
                    isIndividual: request.isIndividual || model?.requestData?.isIndividual,
                    resourceCodes: request.resourceCodes || model?.requestData?.resourceCodes,
                };

                await getPagedInvitation(newDataReq);

            }
        )();
    }, [request, userInfo?.userProfile?.organizationId]);

    const getPagedInvitation = async (newDataReq?: IRequestGetPagedInvitation) => {
        try {
            // onShowLoading();
            setLoadingDataTable(true);

            const pageSize = newDataReq?.pageSize || Constants.ROW_PER_PAGE;
            const pageNumber = Helpers.getPageNumber(newDataReq?.pageNumber || 1, pageSize, newDataReq?.totalCount || 0);

            const reqTemp: IRequestGetPagedInvitation = {
                ...newDataReq,

                pageSize: pageSize,
                pageNumber: pageNumber || 1,

                // clientId,
                tenantCode,
                serviceCode,
                organizationId: userInfo?.userProfile?.organizationId,
            };
            const resultGetPaged = await InvitationService.getPagedTenant(reqTemp);

            setModel({
                requestData: {
                    ...newDataReq,
                    pageSize: resultGetPaged?.pageSize,
                    totalCount: resultGetPaged?.totalCount,
                    pageNumber: resultGetPaged?.currentPage,
                },
                requestDataTemp: {
                    ...newDataReq,
                    pageSize: resultGetPaged?.pageSize,
                    totalCount: resultGetPaged?.totalCount,
                    pageNumber: resultGetPaged?.currentPage,
                },
            });

            let query: string = `?pageNumber=${pageNumber}&pageSize=${pageSize}`;

            if (!Helpers.isNullOrEmpty(newDataReq?.status)) {
                query += `&status=${newDataReq?.status}`;
            }
            if (!Helpers.isNullOrEmpty(newDataReq?.validTo)) {
                query += `&validTo=${newDataReq?.validTo}`;
            }
            if (!Helpers.isNullOrEmpty(newDataReq?.validFrom)) {
                query += `&validFrom=${newDataReq?.validFrom}`;
            }
            if (!Helpers.isNullOrEmpty(newDataReq?.searchText)) {
                query += `&searchText=${newDataReq?.searchText}`;
            }

            setDataRows(resultGetPaged.items);
            onGetPaged(query, resultGetPaged.totalCount);
        } catch (error) {
            const e = Helpers.renderExceptionError(error);
            onError(e);
        } finally {
            // onHideLoading();
            setLoadingDataTable(false);
        }
    };

    const renderValueDate = (value: any) => {
        return (Helpers.isNullOrEmpty(value) || value === "0" || value === 0)
            ? undefined : value * 1000;
    }

    const onChangeValue = (newValue: any, key: string) => {
        setModel(prev => ({
            ...prev,
            requestDataTemp: {
                ...prev.requestDataTemp,
                [key]: newValue,
            }
        }));
    };

    const renderFilterForm = () => {
        return (
            <Box>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <FormField
                            maxLength={255}
                            defaultValue={model.requestDataTemp?.searchText}
                            label={strings.format(strings.Staff.SEARCH_TEXT, lable)}
                            placeholder={strings.format(strings.Staff.ENTER_SEARCH_TEXT, lable)}
                            onBlur={(value: any) => onChangeValue(value, "searchText")}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Autocomplete
                            data={listStatusInvitation || []}
                            label={strings.Common.STATUS}
                            placeholder={strings.Common.STATUS}
                            defaultValue={model.requestDataTemp?.status}
                            onChange={(value) => onChangeValue(value, "status")}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <DatePicker
                            label={Strings.INVITATION.VALID_FROM}
                            placeholder={Strings.INVITATION.VALID_FROM}
                            value={renderValueDate(model.requestDataTemp?.validFrom)}
                            onChangeValue={(value: any) => {
                                onChangeValue(value ? moment(value).unix() : undefined, "validFrom")
                            }}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <DatePicker
                            label={Strings.INVITATION.VALID_TO}
                            placeholder={Strings.INVITATION.VALID_TO}
                            value={renderValueDate(model.requestDataTemp?.validTo)}
                            onChangeValue={(value: any) => {
                                onChangeValue(value ? moment(value).unix() : undefined, "validTo")
                            }}
                        />
                    </Grid>
                </Grid>
            </Box>
        );
    };

    const handleCopyLink = (invitedUrl?: string) => {
        navigator.clipboard.writeText(invitedUrl).then(() => {
            setCopyContentTooltip(true);
            setTimeout(function () {
                setCopyContentTooltip(false);
            }, 1000);
        })
    }

    const handleDelete = async (invite: IInvitation) => {
        if (Helpers.isFunction(deleteInvitation)) {
            deleteInvitation(invite, () => getPagedInvitation(model.requestDataTemp));
        } else {
            onDeleteInvitation({
                id: invite.id,
                onCallBack() {
                    getPagedInvitation(model.requestDataTemp);
                },
            })
        }
    }

    return (
        <MainComponent>
            <>
                {(!Helpers.isNullOrEmpty(model.requestData?.validFrom) ||
                    !Helpers.isNullOrEmpty(model.requestData?.validTo) ||
                    !Helpers.isNullOrEmpty(model.requestData?.status)) && (
                        <Box mb={1}>
                            {!Helpers.isNullOrEmpty(model.requestData?.status) && (
                                <ControlChip
                                    label={strings.Common.STATUS}
                                    value={(listStatusInvitation || []).find((el) => el.code === model.requestData?.status)?.name}
                                    onDelete={() => {
                                        getPagedInvitation({ ...model.requestData, status: undefined });
                                    }}
                                />
                            )}
                            {
                                !Helpers.isNullOrEmpty(model.requestData?.validFrom) &&
                                <ControlChip
                                    label={Strings.INVITATION.VALID_FROM}
                                    value={moment(renderValueDate(model.requestData?.validFrom)).format("DD-MM-YYYY")}
                                    onDelete={() => {
                                        getPagedInvitation({ ...model.requestData, validFrom: undefined });
                                    }}
                                />
                            }
                            {
                                !Helpers.isNullOrEmpty(model.requestData?.validTo) &&
                                <ControlChip
                                    label={Strings.INVITATION.VALID_TO}
                                    value={moment(renderValueDate(model.requestData?.validTo)).format("DD-MM-YYYY")}
                                    onDelete={() => {
                                        getPagedInvitation({ ...model.requestData, validTo: undefined });
                                    }}
                                />
                            }
                        </Box>
                    )}
                <Card>
                    <Box p={2}>
                        <DataTable
                            table={dataTable}
                            loading={loadingDataTable}
                            rowPerPage={model.requestData?.pageSize}
                            pageNumber={model.requestData?.pageNumber}
                            totalCount={model.requestData?.totalCount}

                            onChangePageSize={(pageSize) => {
                                getPagedInvitation({ ...model.requestData, pageSize })
                            }}
                            onChangePageNumber={(pageNumber) => {
                                getPagedInvitation({ ...model.requestData, pageNumber })
                            }}

                            searchText={model.requestData?.searchText || ""}
                            placeholderSearch={strings.format(strings.Staff.SEARCH_TEXT, lable)}
                            onSearch={(val) => {
                                getPagedInvitation({ ...model.requestData, pageNumber: 1, searchText: val })
                            }}
                            onSearchChange={(val) => {
                                setModel(prev => ({
                                    ...prev,
                                    requestData: {
                                        ...prev.requestData,
                                        searchText: val,
                                    }
                                }));
                            }}

                            filterForm={renderFilterForm()}
                            onFilter={() => {
                                getPagedInvitation({ ...model.requestDataTemp, pageNumber: 1 })
                            }}
                            onReset={() => {
                                getPagedInvitation({ pageNumber: 1, pageSize: Constants.ROW_PER_PAGE });
                            }}
                            onCloseFilter={() => {
                                setModel(prev => ({ ...prev, requestDataTemp: prev.requestData }));
                            }}

                            actionList={(row) => {
                                return [
                                    {
                                        key: "copy",
                                        iconName: "copy",
                                        title: strings.INVITATION.COPY_INVITATION_URL,
                                        callback: (row: IInvitation) => handleCopyLink(row.invitedUrl),
                                        tooltip: showCopyContentTooltip && "Sao chép thành công!",
                                        disabled: (row.status !== Status.Active && row.status !== Status.Decline)
                                            || (Number(row.validTo) < moment().unix())
                                            || Helpers.isNullOrEmpty(row.invitedUrl)
                                    },
                                    {
                                        key: "resend",
                                        iconName: "resend",
                                        title: strings.INVITATION.RESEND_INVITATION,
                                        callback: (row: IInvitation) => {
                                            onResendInvitation({
                                                id: row.id,
                                                onCallBack() {
                                                    getPagedInvitation(model.requestDataTemp)
                                                },
                                            })
                                        },
                                        disabled: row.status !== Status.Active && row.status !== Status.Decline
                                    },
                                    {
                                        key: "delete",
                                        iconName: "delete",
                                        title: strings.Common.DELETE,
                                        disabled: row.status !== Status.Active,
                                        callback: (row: IInvitation) => handleDelete(row),
                                    }
                                ] as IActionMenuItem[];
                            }}
                        />
                    </Box>
                </Card>
            </>
        </MainComponent>
    );
};

export default ManageInvitationContainer;